import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useParties } from "@/contexts/PartyContext";
import { Ledger } from "@/lib/accounting/types";
import { Party } from "@/types/party";
import { QuickPartyModal } from "@/components/accounting/QuickPartyModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, Search, Building2, User, MapPin, ChevronDown, PlusCircle, Plus,
  ShieldCheck, AlertCircle, X, Check
} from "lucide-react";

interface PartyAutocompleteProps {
  value: string;
  onSelect: (ledgerId: string, ledger: Ledger, party?: Party) => void;
  partyType: "customer" | "supplier";
  placeholder?: string;
  className?: string;
}

export function PartyAutocomplete({
  value,
  onSelect,
  partyType,
  placeholder,
  className,
}: PartyAutocompleteProps) {
  const { ledgers } = useAccounting();
  const { parties, getPartiesByType } = useParties();

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateInitialName, setQuickCreateInitialName] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter parties by relevant type (Customer, Supplier, or Both)
  const typedParties = useMemo(() => {
    return getPartiesByType(partyType);
  }, [getPartiesByType, partyType]);

  // Combine parties (source of truth) with any individual accounting ledgers
  const combinedList = useMemo(() => {
    const list: {
      id: string;
      ledgerId: string;
      name: string;
      displayName?: string;
      partyType?: string;
      entityType?: string;
      gstin?: string;
      state?: string;
      city?: string;
      contactPerson?: string;
      phone?: string;
      email?: string;
      balance: number;
      balanceType: "Dr" | "Cr";
      party?: Party;
      ledger?: Ledger;
    }[] = [];

    // 1. Add from Parties Management (primary source of truth)
    for (const p of typedParties) {
      if (p.status === "archived") continue;

      const ledgerId = partyType === "customer"
        ? (p.debtorLedgerId || p.id)
        : (p.creditorLedgerId || p.id);

      const linkedLedger = ledgers.find((l) => l.id === ledgerId);
      const balance = linkedLedger
        ? linkedLedger.currentBalance
        : (partyType === "customer" ? (p.debtorCurrentBalance || p.currentBalance) : (p.creditorCurrentBalance || p.currentBalance));
      const balanceType = linkedLedger
        ? linkedLedger.currentBalanceType
        : (partyType === "customer" ? (p.debtorBalanceType || "Dr") : (p.creditorBalanceType || "Cr"));

      list.push({
        id: p.id,
        ledgerId,
        name: p.name || "",
        displayName: p.displayName || p.name || "",
        partyType: p.partyType,
        entityType: p.entityType || "business",
        gstin: p.gstin,
        state: p.billingState || p.shippingState,
        city: p.billingCity || p.shippingCity,
        contactPerson: p.contactPerson,
        phone: p.phone,
        email: p.email,
        balance: balance || 0,
        balanceType: (balanceType === "Cr" ? "Cr" : "Dr"),
        party: p,
        ledger: linkedLedger,
      });
    }

    // 2. Add individual Sundry Debtors / Creditors ledgers not yet linked to a party record
    // CRITICAL: Strictly exclude system control parent accounts!
    const relevantLedgers = ledgers.filter((l) => {
      if (partyType === "customer") {
        if (l.group !== "Sundry Debtors") return false;
      } else {
        if (l.group !== "Sundry Creditors") return false;
      }

      const lowerName = (l.name || "").toLowerCase().trim();
      // Exclude generic placeholder ledgers
      if (
        lowerName === "sundry debtors" ||
        lowerName === "sundry debtors (customers)" ||
        lowerName === "sundry creditors" ||
        lowerName === "sundry creditors (suppliers)"
      ) {
        return false;
      }
      if (l.isSystem && (lowerName.includes("sundry debtor") || lowerName.includes("sundry creditor"))) {
        return false;
      }
      return true;
    });

    for (const l of relevantLedgers) {
      if (!list.some((item) => item.ledgerId === l.id || (item.name && item.name.toLowerCase() === (l.name || "").toLowerCase()))) {
        list.push({
          id: (l as any).partyId || l.id,
          ledgerId: l.id,
          name: l.name || "",
          displayName: l.name || "",
          gstin: l.gstin,
          state: l.state,
          phone: l.phone,
          balance: l.currentBalance || 0,
          balanceType: l.currentBalanceType === "Cr" ? "Cr" : "Dr",
          ledger: l,
        });
      }
    }

    return list;
  }, [typedParties, ledgers, partyType]);

  const selectedItem = useMemo(() => {
    return combinedList.find((item) => item.ledgerId === value || item.id === value);
  }, [combinedList, value]);

  // Sync display text when value changes externally
  useEffect(() => {
    if (selectedItem && !isOpen) {
      setQuery(selectedItem.displayName || selectedItem.name);
    } else if (!value && !isOpen) {
      setQuery("");
    }
  }, [selectedItem, value, isOpen]);

  // Filter based on query
  const filtered = useMemo(() => {
    if (!query.trim()) return combinedList;
    const q = query.toLowerCase().trim();
    return combinedList
      .filter((item) => {
        const nameMatch = (item.name || "").toLowerCase().includes(q);
        const displayMatch = (item.displayName || "").toLowerCase().includes(q);
        const gstinMatch = item.gstin ? item.gstin.toLowerCase().includes(q) : false;
        const stateMatch = item.state ? item.state.toLowerCase().includes(q) : false;
        const cityMatch = item.city ? item.city.toLowerCase().includes(q) : false;
        const contactMatch = item.contactPerson ? item.contactPerson.toLowerCase().includes(q) : false;
        const phoneMatch = item.phone ? item.phone.includes(q) : false;
        const emailMatch = item.email ? item.email.toLowerCase().includes(q) : false;

        return nameMatch || displayMatch || gstinMatch || stateMatch || cityMatch || contactMatch || phoneMatch || emailMatch;
      })
      .sort((a, b) => {
        const aName = (a.displayName || a.name || "").toLowerCase();
        const bName = (b.displayName || b.name || "").toLowerCase();
        const aStart = aName.startsWith(q) ? 0 : 1;
        const bStart = bName.startsWith(q) ? 0 : 1;
        return aStart - bStart || aName.localeCompare(bName);
      });
  }, [combinedList, query]);

  // Check if query is an exact match for an existing party
  const hasExactMatch = useMemo(() => {
    if (!query.trim()) return false;
    const q = query.trim().toLowerCase();
    return combinedList.some(
      (item) => (item.name || "").toLowerCase() === q || (item.displayName || "").toLowerCase() === q
    );
  }, [combinedList, query]);

  const showInlineCreate = query.trim().length > 0 && !hasExactMatch;

  useEffect(() => {
    setHighlightIndex(0);
  }, [filtered.length, query]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-party-item]");
    items[highlightIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, isOpen]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        if (selectedItem) {
          setQuery(selectedItem.displayName || selectedItem.name);
        } else if (!value) {
          setQuery("");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [selectedItem, value]);

  const handleSelectItem = useCallback(
    (item: typeof combinedList[0]) => {
      setQuery(item.displayName || item.name);
      setIsOpen(false);

      const targetLedger: Ledger = (item.ledger || {
        id: item.ledgerId,
        name: item.name,
        group: partyType === "customer" ? "Sundry Debtors" : "Sundry Creditors",
        nature: partyType === "customer" ? "Asset" : "Liability",
        openingBalance: 0,
        openingBalanceType: item.balanceType,
        currentBalance: item.balance,
        currentBalanceType: item.balanceType,
        gstin: item.gstin,
        state: item.state,
        phone: item.phone,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }) as Ledger;

      onSelect(item.ledgerId, targetLedger, item.party);
      inputRef.current?.blur();
    },
    [onSelect, partyType]
  );

  const openQuickCreate = (initialText?: string) => {
    setQuickCreateInitialName(initialText || query.trim());
    setIsOpen(false);
    setQuickCreateOpen(true);
  };

  const handlePartyCreated = (party: Party, ledgerId: string) => {
    const partyName = party.displayName || party.name;
    setQuery(partyName);

    const createdLedger: Ledger = {
      id: ledgerId,
      name: party.name,
      group: partyType === "customer" ? "Sundry Debtors" : "Sundry Creditors",
      nature: partyType === "customer" ? "Asset" : "Liability",
      openingBalance: party.openingBalance || 0,
      openingBalanceType: party.balanceType === "Cr" ? "Cr" : "Dr",
      currentBalance: party.currentBalance || 0,
      currentBalanceType: party.balanceType === "Cr" ? "Cr" : "Dr",
      gstin: party.gstin,
      state: party.billingState || party.shippingState,
      address: party.billingAddress || party.shippingAddress,
      phone: party.phone,
      email: party.email,
      createdAt: party.createdAt,
      updatedAt: party.updatedAt,
    } as Ledger;

    onSelect(ledgerId, createdLedger, party);
    inputRef.current?.focus();
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      if (!isOpen) return;

      const totalItems = filtered.length + (showInlineCreate ? 1 : 0);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightIndex((i) => Math.min(i + 1, Math.max(0, totalItems - 1)));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightIndex((i) => Math.max(i - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (showInlineCreate && highlightIndex === 0) {
            openQuickCreate(query.trim());
          } else {
            const actualIdx = showInlineCreate ? highlightIndex - 1 : highlightIndex;
            if (filtered[actualIdx]) {
              handleSelectItem(filtered[actualIdx]);
            } else if (query.trim()) {
              openQuickCreate(query.trim());
            }
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          if (selectedItem) {
            setQuery(selectedItem.displayName || selectedItem.name);
          } else if (!value) {
            setQuery("");
          }
          break;
        case "Tab":
          setIsOpen(false);
          if (showInlineCreate && highlightIndex === 0) {
            openQuickCreate(query.trim());
          } else {
            const actualIdx = showInlineCreate ? highlightIndex - 1 : highlightIndex;
            if (filtered[actualIdx]) {
              handleSelectItem(filtered[actualIdx]);
            }
          }
          break;
      }
    },
    [isOpen, filtered, highlightIndex, showInlineCreate, query, selectedItem, value, handleSelectItem]
  );

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setQuery("");
    onSelect("", {} as any, undefined);
    inputRef.current?.focus();
  };

  return (
    <>
      <div ref={containerRef} className="relative">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onClick={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || `Select or type to search ${partyType === "customer" ? "Customer" : "Supplier"}...`}
            className={`h-9 w-full pl-8 pr-14 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-primary text-foreground placeholder:text-muted-foreground ${className || ""}`}
            data-field="party-input"
            autoComplete="off"
          />

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {query && (
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear selection"
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
            <ChevronDown
              onClick={() => {
                setIsOpen((o) => !o);
                inputRef.current?.focus();
              }}
              className="h-3.5 w-3.5 text-muted-foreground cursor-pointer hover:text-foreground transition-transform"
            />
          </div>
        </div>

        {isOpen && (
          <div
            ref={listRef}
            className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-border bg-popover shadow-xl text-foreground"
          >
            {/* Inline Quick Create Option when typed name doesn't exist */}
            {showInlineCreate && (
              <div
                data-party-item
                onClick={() => openQuickCreate(query.trim())}
                onMouseEnter={() => setHighlightIndex(0)}
                className={`p-2.5 border-b border-border transition-colors cursor-pointer flex items-center justify-between ${
                  highlightIndex === 0
                    ? "bg-primary/10 text-primary font-medium"
                    : "bg-primary/5 hover:bg-primary/10 text-primary"
                }`}
              >
                <div className="flex items-center gap-2 text-xs font-semibold">
                  <PlusCircle className="w-4 h-4 text-primary shrink-0" />
                  <span className="truncate">
                    + Add New {partyType === "customer" ? "Customer" : "Supplier"}: <strong>"{query.trim()}"</strong>
                  </span>
                </div>
                <span className="text-[10px] text-muted-foreground font-mono shrink-0 ml-2">Press Enter</span>
              </div>
            )}

            {filtered.length === 0 && !showInlineCreate ? (
              <div className="p-4 text-center space-y-2.5">
                <Users className="h-6 w-6 text-muted-foreground/40 mx-auto" />
                <p className="text-xs text-muted-foreground">
                  {combinedList.length === 0
                    ? `No ${partyType}s found for this company.`
                    : `No ${partyType}s match "${query}".`}
                </p>
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => openQuickCreate(query.trim())}
                  className="text-xs gap-1.5 h-7 border-primary/30 text-primary hover:bg-primary/5 mx-auto"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Create New {partyType === "customer" ? "Customer" : "Supplier"}</span>
                </Button>
              </div>
            ) : (
              <>
                <div className="px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border bg-muted/40 flex items-center justify-between">
                  <span>{filtered.length} {partyType}{filtered.length !== 1 ? "s" : ""}</span>
                  <span>↑↓ navigate · Enter select</span>
                </div>

                {filtered.map((item, idx) => {
                  const actualIdx = showInlineCreate ? idx + 1 : idx;
                  const isHighlighted = actualIdx === highlightIndex;
                  const isSelected = item.ledgerId === value || item.id === value;
                  const balance = item.balance;
                  const balanceType = item.balanceType;
                  const initial = ((item.displayName || item.name || "?").charAt(0) || "?").toUpperCase();

                  return (
                    <div
                      key={item.ledgerId || item.id}
                      data-party-item
                      onClick={() => handleSelectItem(item)}
                      onMouseEnter={() => setHighlightIndex(actualIdx)}
                      className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors border-b border-border/40 last:border-0 ${
                        isHighlighted
                          ? "bg-accent/15 border-l-2 border-l-primary"
                          : isSelected
                          ? "bg-muted/60 border-l-2 border-l-primary"
                          : "border-l-2 border-l-transparent hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 border border-primary/20 shrink-0 text-primary font-bold text-[10px]">
                        {initial}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-semibold text-foreground truncate">
                            {highlightMatch(item.displayName || item.name, query)}
                          </span>
                          {item.displayName && item.displayName !== item.name && (
                            <span className="text-[10px] text-muted-foreground truncate">
                              ({item.name})
                            </span>
                          )}
                          {item.entityType === "individual" && (
                            <span className="text-[9px] px-1 py-0.2 rounded bg-sky-500/10 text-sky-600 font-medium">
                              Individual
                            </span>
                          )}
                          {item.partyType === "both" && (
                            <span className="text-[9px] px-1 py-0.2 rounded bg-purple-500/10 text-purple-600 font-medium">
                              Dual
                            </span>
                          )}
                          {item.gstin && (
                            <span className="text-[9px] px-1.5 py-0.2 rounded bg-muted font-mono text-muted-foreground">
                              {item.gstin}
                            </span>
                          )}
                          {isSelected && (
                            <Check className="w-3 h-3 text-primary ml-auto" />
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5 flex-wrap">
                          {(item.city || item.state) && (
                            <span className="flex items-center gap-0.5">
                              <MapPin className="h-2.5 w-2.5" />
                              {[item.city, item.state].filter(Boolean).join(", ")}
                            </span>
                          )}
                          {item.contactPerson && (
                            <span className="truncate">Contact: {item.contactPerson}</span>
                          )}
                          {item.phone && <span>Ph: {item.phone}</span>}
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <span
                          className={`text-xs font-semibold font-mono ${
                            balance > 0
                              ? balanceType === "Dr"
                                ? "text-amber-600"
                                : "text-emerald-600"
                              : "text-muted-foreground"
                          }`}
                        >
                          ₹{Math.abs(balance).toLocaleString("en-IN")}
                        </span>
                        <span className="text-[9px] text-muted-foreground block">
                          {balanceType === "Dr" ? "Receivable" : "Payable / Adv"}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Persistent Bottom Add Action */}
                <div className="p-2 border-t border-border bg-muted/25 flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => openQuickCreate(query.trim())}
                    className="text-xs h-7 gap-1 text-primary hover:text-primary hover:bg-primary/10 w-full justify-start font-medium"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>+ Add New {partyType === "customer" ? "Customer" : "Supplier"}</span>
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Quick Party Creation Modal */}
      <QuickPartyModal
        open={quickCreateOpen}
        onOpenChange={setQuickCreateOpen}
        partyType={partyType}
        initialName={quickCreateInitialName}
        onPartyCreated={handlePartyCreated}
      />
    </>
  );
}

function highlightMatch(text: string | undefined | null, query: string) {
  if (!text) return "";
  if (!query || !query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase().trim());
  if (idx === -1) return text;
  const qLen = query.trim().length;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-primary/20 text-primary font-semibold rounded-sm px-0.5">
        {text.slice(idx, idx + qLen)}
      </span>
      {text.slice(idx + qLen)}
    </>
  );
}
